import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BedDouble, Bath, Ruler, MapPin, Calendar, Building } from "lucide-react";
import { api } from "../../lib/api";
import { Wordmark, GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import LeadForm from "../../components/shared/LeadForm";
import OpportunityCard from "../../components/shared/OpportunityCard";
import AskAuriAboutThis from "../../components/shared/AskAuriAboutThis";

const fmtPrice = (o) => {
  if (o.request_pricing_only || !o.price) return "Request Pricing";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: o.currency || "USD", maximumFractionDigits: 0 }).format(o.price);
};

export default function OpportunityDetail() {
  const { slug } = useParams();
  const [o, setO] = useState(null);
  const [related, setRelated] = useState([]);
  const [gIndex, setGIndex] = useState(0);

  useEffect(() => {
    setO(null);
    api.get(`/public/opportunities/${slug}`).then((r) => setO(r.data)).catch(() => setO(false));
  }, [slug]);

  useEffect(() => {
    if (!o) return;
    api.get(`/public/opportunities?market=${encodeURIComponent(o.country)}`).then((r) => setRelated(r.data.filter((x) => x.slug !== o.slug).slice(0, 3))).catch(() => {});
  }, [o]);

  if (o === null) return <div className="container-editorial py-32">Loading…</div>;
  if (o === false) return (
    <div className="container-editorial py-32">
      <h1 className="font-editorial text-4xl">Opportunity not found.</h1>
      <Link to="/opportunities" className="link-underline mt-4 inline-block">View all opportunities</Link>
    </div>
  );

  const images = [o.featured_image, ...(o.gallery || [])].filter(Boolean);
  const active = images[gIndex] || images[0];

  return (
    <div data-testid={`page-opportunity-${o.slug}`}>
      <section className="bg-altura-ivory py-10 border-b border-altura-espresso/10">
        <div className="container-editorial">
          <Link to="/opportunities" className="text-xs uppercase tracking-widest text-altura-espresso/60 hover:text-altura-espresso">← All Opportunities</Link>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <Eyebrow>{o.country}{o.city ? ` · ${o.city}` : ""}{o.area ? ` · ${o.area}` : ""}</Eyebrow>
              <EditorialHeading size="lg" className="mt-3">{o.title}</EditorialHeading>
              {o.ref_no && <div className="mt-2 text-xs font-mono text-altura-charcoal/50">Ref · {o.ref_no}</div>}
            </div>
            <div className="text-right">
              <div className="eyebrow">Price</div>
              <div className="mt-1 font-editorial text-3xl text-altura-espresso">{fmtPrice(o)}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-altura-ivory py-14">
        <div className="container-editorial grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="aspect-[16/10] overflow-hidden rounded-lg bg-altura-beige">
              <img src={active} alt={o.title} className="h-full w-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-6 gap-2">
                {images.slice(0, 6).map((src, i) => (
                  <button key={src} onClick={() => setGIndex(i)}
                          className={`aspect-square overflow-hidden rounded ${i === gIndex ? "ring-2 ring-altura-gold" : "opacity-80 hover:opacity-100"}`}
                          data-testid={`gallery-thumb-${i}`}>
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-10">
              <div className="flex flex-wrap gap-4 border-y border-altura-espresso/10 py-5">
                {o.bedrooms ? <Stat icon={BedDouble} label="Bedrooms" value={o.bedrooms} /> : null}
                {o.bathrooms ? <Stat icon={Bath} label="Bathrooms" value={o.bathrooms} /> : null}
                {o.area_sqm ? <Stat icon={Ruler} label="Area" value={`${o.area_sqm} m²`} /> : null}
                {o.land_size_sqm ? <Stat icon={Ruler} label="Land" value={`${o.land_size_sqm} m²`} /> : null}
                {o.completion_date ? <Stat icon={Calendar} label="Completion" value={o.completion_date} /> : null}
                {o.area ? <Stat icon={MapPin} label="Location" value={o.area} /> : null}
              </div>

              <div className="mt-10">
                <Eyebrow>About this opportunity</Eyebrow>
                <p className="mt-5 whitespace-pre-line leading-relaxed text-altura-charcoal/80">{o.description || o.public_summary}</p>
              </div>

              {o.amenities?.length > 0 && (
                <div className="mt-10">
                  <Eyebrow>Amenities & Features</Eyebrow>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {o.amenities.map((a) => <span key={a} className="badge-soft">{a}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 rounded-lg border border-altura-espresso/15 bg-white p-6">
              <Eyebrow>Inquire</Eyebrow>
              <div className="mt-2 font-editorial text-2xl text-altura-espresso">Request details</div>
              <GoldDivider className="mt-4" />
              <div className="mt-5">
                <LeadForm variant="opportunity" opportunity={o} sourcePage={`/opportunities/${o.slug}`} testIdPrefix="opp-inquiry" />
              </div>
              <p className="mt-4 text-[11px] text-altura-charcoal/60">
                Altura Global does not provide legal, tax, immigration, or financial advice. We coordinate with licensed local professionals.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-altura-ivory pb-16 pt-4">
        <div className="container-editorial">
          <AskAuriAboutThis kind="opportunity" title={o.title} slug={o.slug} />
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-altura-beige/40 py-24">
          <div className="container-editorial">
            <div className="mb-10 font-editorial text-3xl text-altura-espresso">More from {o.country}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => <OpportunityCard key={r.id} opportunity={r} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="min-w-[140px]">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50"><Icon className="h-3 w-3 text-altura-gold-dark" />{label}</div>
      <div className="mt-1 font-editorial text-lg text-altura-espresso">{value}</div>
    </div>
  );
}
